// Ubicación en el repo: src/components/AppearanceSettings.jsx
// Sub-pestaña "Apariencia" de Configuración — mismo contenido que la
// sección homónima del prototipo (línea ~2711): modo oscuro (con vista
// previa inmediata al tildar, igual que toggleDarkModePreview()), colores
// de fondo/primario/acento/encabezados, Guardar/Restaurar. Solo Admin puede
// guardar (RLS clinic_settings_write) — el resto de la app ya recibe estos
// colores aplicados desde Layout.jsx al cargar, sin necesitar pasar por acá.
import { useState } from 'react';
import { DEFAULT_SETTINGS, updateClinicSettings, applyClinicSettings } from '../lib/clinicSettings';

export default function AppearanceSettings({ settings, isAdmin, actorId, onSaved }) {
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Vista previa inmediata para cualquier cambio (colores incluidos, no
  // solo modo oscuro como en el prototipo) — mejora pedida por el usuario:
  // elegir un color y no ver nada cambiar hasta Guardar generaba confusión.
  // Guardar/Restaurar siguen siendo los únicos que persisten en la base.
  function set(field, value) {
    const next = { ...form, [field]: value };
    setForm(next);
    applyClinicSettings(next);
  }

  async function handleSave() {
    setError('');
    setSaving(true);
    try {
      await updateClinicSettings(form, actorId);
      applyClinicSettings(form);
      onSaved(form);
    } catch (err) {
      setError(err.message || 'No se pudo guardar la apariencia.');
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    setError('');
    setSaving(true);
    try {
      await updateClinicSettings(DEFAULT_SETTINGS, actorId);
      setForm(DEFAULT_SETTINGS);
      applyClinicSettings(DEFAULT_SETTINGS);
      onSaved(DEFAULT_SETTINGS);
    } catch (err) {
      setError(err.message || 'No se pudo restaurar la apariencia.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="patients-page">
      <h1>Apariencia</h1>

      {!isAdmin && (
        <p className="text-muted">Solo un Admin puede cambiar la apariencia de la clínica.</p>
      )}

      <div className="field" style={{ marginBottom: 18, opacity: isAdmin ? 1 : 0.6, pointerEvents: isAdmin ? 'auto' : 'none' }}>
        <label className="field--checkbox">
          <input type="checkbox" checked={form.darkMode} onChange={(e) => set('darkMode', e.target.checked)} />
          🌙 Modo oscuro
        </label>
      </div>

      <div
        className="form-row"
        style={form.darkMode ? { opacity: 0.4, pointerEvents: 'none' } : undefined}
      >
        <label className="field">
          <span>Color de fondo</span>
          <input type="color" value={form.bgColor} onChange={(e) => set('bgColor', e.target.value)} />
        </label>
        <label className="field">
          <span>Color primario (botones principales)</span>
          <input type="color" value={form.primaryColor} onChange={(e) => set('primaryColor', e.target.value)} />
        </label>
      </div>

      <div className="form-row">
        <label className="field">
          <span>Color de acento (bordes activos, resaltados)</span>
          <input type="color" value={form.secondaryColor} onChange={(e) => set('secondaryColor', e.target.value)} />
        </label>
        <label className="field">
          <span>Color de encabezados y menú activo</span>
          <input type="color" value={form.sidebarColor} onChange={(e) => set('sidebarColor', e.target.value)} />
        </label>
      </div>

      <p className="field-hint">
        El color de fondo no aplica en modo oscuro (usa una paleta oscura fija); los demás colores sí se
        mantienen.
      </p>

      {error && <p className="auth-error">{error}</p>}

      {isAdmin && (
        <div className="modal-actions" style={{ justifyContent: 'flex-start' }}>
          <button type="button" className="button-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar apariencia'}
          </button>
          <button type="button" className="button-secondary" onClick={handleReset} disabled={saving}>
            Restaurar colores originales
          </button>
        </div>
      )}
    </div>
  );
}
